import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../Login/Login.scss";
import axiosClient from "../../axiosClient";
import { brand, brandLogos, platform } from "../../brand";
import {
  isPasswordValid,
  PASSWORD_REQUIREMENTS_HINT,
  PASSWORD_VALIDATION_ERROR,
} from "../../utils/passwordRequirements";

export const ResetPassword = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    if (!token) {
      setFormError("Invalid or expired reset link.");
      return;
    }

    if (!isPasswordValid(password)) {
      setFormError(PASSWORD_VALIDATION_ERROR);
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      await axiosClient.post("/auth/reset-password", {
        token,
        platform,
        email: email.trim(),
        password,
      });
      setIsSuccess(true);
    } catch (err: unknown) {
      const apiMessage =
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response
          ?.data?.message === "string"
          ? (err as { response: { data: { message: string } } }).response.data
              .message
          : null;

      setFormError(apiMessage ?? "Invalid or expired reset link.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page" id="resetPasswordPage">
      <div className="login-card">
        <Link to="/" className="login-logo" aria-label={`${brand.displayName} home`}>
          <img src={brandLogos.color} alt={brand.displayName} />
        </Link>
        {!isSuccess && (
          <p className="login-tagline">Choose a new password</p>
        )}

        {isSuccess ? (
          <div className="login-success-state">
            <p className="login-success-panel" role="status">
              Your password has been updated successfully. You can now sign in
              with your new password.
            </p>
            <Link to="/login" className="login-submit">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                placeholder="Enter your account email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFormError("");
                }}
                autoComplete="email"
                autoFocus
                required
              />
            </div>

            <div className="login-field">
              <label htmlFor="reset-password">New password</label>
              <input
                id="reset-password"
                type="password"
                aria-describedby="reset-password-requirements"
                placeholder="Enter your new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFormError("");
                }}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <p className="login-field-hint" id="reset-password-requirements">
                {PASSWORD_REQUIREMENTS_HINT}
              </p>
            </div>

            <div className="login-field">
              <label htmlFor="reset-confirm-password">Confirm password</label>
              <input
                id="reset-confirm-password"
                type="password"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFormError("");
                }}
                autoComplete="new-password"
                required
              />
            </div>

            {formError && (
              <p className="login-error" role="alert">
                {formError}
              </p>
            )}

            <button
              type="submit"
              className="login-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        {!isSuccess && (
          <p className="login-footer">
            <Link to="/login">Back to sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
};
